import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  StarIcon, 
  DocumentIcon, 
  FolderIcon, 
  MagnifyingGlassIcon,
  ArrowLeftIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { DocumentNode } from '../../types/documents';
import { documentService } from '../../services/documentService';
import { useToast } from '../../components/UI/ToastProvider';
import { getFileTypeIcon } from '../../components/Documents/fileTypeIcons';
import { getWorkflowStatusBadgeClasses, getWorkflowStatusLabel } from '../../utils/workflowStatusBadge';

const FavoritesPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToast();
  
  const [favoriteDocuments, setFavoriteDocuments] = useState<DocumentNode[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [removingFavorites, setRemovingFavorites] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');

  // Carregar documentos favoritos
  useEffect(() => {
    loadFavoriteDocuments();
  }, []);

  const loadFavoriteDocuments = async () => {
    try {
      setIsLoading(true);
      const documents = await documentService.getFavoriteDocuments();
      setFavoriteDocuments(documents);
    } catch (error) {
      console.error('Erro ao carregar favoritos:', error);
      addToast('Erro ao carregar documentos favoritos', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Filtrar documentos baseado na busca
  const filteredDocuments = favoriteDocuments.filter(doc =>
    doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    doc.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Navegar para detalhes do documento
  const handleDocumentClick = (document: DocumentNode) => {
    // Salvar a página atual no state para voltar depois
    navigate(`/documents/${document.id}/details`, { 
      state: { 
        returnTo: location.pathname,
        returnToLabel: 'Favoritos'
      } 
    });
  };

  // Remover dos favoritos
  const handleRemoveFromFavorites = async (documentId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    
    setRemovingFavorites(prev => new Set(prev).add(documentId));
    
    try {
      await documentService.removeFromFavorites(documentId);
      setFavoriteDocuments(prev => prev.filter(doc => doc.id !== documentId));
      addToast('Documento removido dos favoritos', 'success');
    } catch (error) {
      console.error('Erro ao remover dos favoritos:', error);
      addToast('Erro ao remover dos favoritos', 'error');
    } finally {
      setRemovingFavorites(prev => {
        const newSet = new Set(prev);
        newSet.delete(documentId);
        return newSet;
      });
    }
  };

  // Voltar para a página anterior
  const handleGoBack = () => {
    const returnTo = location.state?.returnTo || '/documents';
    navigate(returnTo);
  };

  const getFileIcon = (nodeType: string, fileName?: string) => {
    if (nodeType === 'folder') {
      return <FolderIcon className="h-8 w-8 text-blue-500" />;
    }
    
    if (fileName) {
      const ext = fileName.split('.').pop()?.toLowerCase() || '';
      return <span className="text-2xl">{getFileTypeIcon(ext)}</span>;
    }
    
    return <DocumentIcon className="h-8 w-8 text-green-500" />;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando favoritos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Botão voltar e título */}
            <div className="flex items-center">
              <button
                onClick={handleGoBack}
                className="mr-4 p-2 text-gray-400 hover:text-gray-600 transition-colors"
                title="Voltar"
              >
                <ArrowLeftIcon className="h-5 w-5" />
              </button>
              <div className="flex items-center">
                <StarIcon className="h-8 w-8 text-yellow-500 mr-3" />
                <div>
                  <h1 className="text-2xl font-semibold text-gray-900">Favoritos</h1>
                  <p className="text-sm text-gray-600">
                    {favoriteDocuments.length} documento{favoriteDocuments.length !== 1 ? 's' : ''} favorito{favoriteDocuments.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Conteúdo principal */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Barra de busca e controles de visualização */}
        <div className="mb-6">
          <div className="flex items-center justify-between gap-4">
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar nos favoritos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            {/* Botões de modo de visualização */}
            <div className="flex items-center space-x-2">
              <button 
                className={`p-2 ${viewMode === 'list' ? 'text-blue-600 bg-blue-50' : 'text-gray-400 hover:text-gray-600'} rounded-md hover:bg-gray-100 transition-colors`}
                onClick={() => setViewMode('list')}
                title="Visualização em lista"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
              </button>
              <button 
                className={`p-2 ${viewMode === 'grid' ? 'text-blue-600 bg-blue-50' : 'text-gray-400 hover:text-gray-600'} rounded-md hover:bg-gray-100 transition-colors`}
                onClick={() => setViewMode('grid')}
                title="Visualização em grade"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Lista de documentos */}
        {filteredDocuments.length === 0 ? (
          <div className="text-center py-12">
            <StarIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchQuery ? 'Nenhum favorito encontrado' : 'Nenhum documento favorito'}
            </h3>
            <p className="text-gray-500">
              {searchQuery 
                ? 'Tente uma busca diferente' 
                : 'Marque documentos como favoritos para vê-los aqui'
              }
            </p>
          </div>
        ) : viewMode === 'list' ? (
          /* Modo Lista */
          <div className="divide-y divide-gray-200">
            {filteredDocuments.map((document) => (
              <div
                key={document.id}
                className="group py-4 flex items-center hover:bg-gray-50 cursor-pointer px-4 rounded-md transition-colors duration-200"
                onClick={() => handleDocumentClick(document)}
              >
                {/* Ícone do documento */}
                <div className="flex-shrink-0 mr-4">
                  {getFileIcon(document.nodeType, document.metadata?.fileName)}
                </div>
                
                {/* Informações do documento */}
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-medium text-gray-900 truncate group-hover:text-blue-600 transition-colors duration-200">
                    {document.title}
                  </h4>
                  <div className="flex items-center mt-1">
                    <span className="text-xs text-gray-500 capitalize">
                      {document.nodeType === 'folder' ? 'Pasta' : 
                       document.nodeType === 'edoc' ? 'Documento Eletrônico' : 
                       'Arquivo'}
                    </span>
                    {document.metadata?.updatedAt && (
                      <>
                        <span className="mx-2 text-gray-300">•</span>
                        <span className="text-xs text-gray-500">{document.metadata.updatedAt}</span>
                      </>
                    )}
                    {document.metadata?.size && document.nodeType !== 'folder' && (
                      <>
                        <span className="mx-2 text-gray-300">•</span>
                        <span className="text-xs text-gray-500">
                          {document.metadata.size}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                
                {/* Status do documento (se aplicável) */}
                {document.status && document.nodeType !== 'folder' && (
                  <div className="ml-4">
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getWorkflowStatusBadgeClasses(
                        document.status
                      )}`}
                    >
                      {getWorkflowStatusLabel(document.status)}
                    </span>
                  </div>
                )}
                
                {/* Ações */}
                <div className="ml-4 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <button
                    onClick={(e) => handleRemoveFromFavorites(document.id, e)}
                    disabled={removingFavorites.has(document.id)}
                    className="p-1 text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50"
                    title="Remover dos favoritos"
                  >
                    {removingFavorites.has(document.id) ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-500"></div>
                    ) : (
                      <TrashIcon className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Modo Grid */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredDocuments.map((document) => (
              <div
                key={document.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow cursor-pointer group"
                onClick={() => handleDocumentClick(document)}
              >
                <div className="p-6">
                  {/* Ícone e ações */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-shrink-0">
                      {getFileIcon(document.nodeType, document.metadata?.fileName)}
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={(e) => handleRemoveFromFavorites(document.id, e)}
                        disabled={removingFavorites.has(document.id)}
                        className="p-1 text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50"
                        title="Remover dos favoritos"
                      >
                        {removingFavorites.has(document.id) ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-500"></div>
                        ) : (
                          <TrashIcon className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Título */}
                  <h3 className="text-lg font-medium text-gray-900 mb-2 line-clamp-2 group-hover:text-blue-600 transition-colors">
                    {document.title}
                  </h3>

                  {/* Descrição */}
                  {document.description && (
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                      {document.description}
                    </p>
                  )}

                  {/* Metadados */}
                  <div className="space-y-1 text-xs text-gray-500">
                    {document.metadata?.fileName && (
                      <div className="flex items-center">
                        <span className="font-medium">Arquivo:</span>
                        <span className="ml-1 truncate">{document.metadata.fileName}</span>
                      </div>
                    )}
                    {document.path && (
                      <div className="flex items-center">
                        <span className="font-medium">Caminho:</span>
                        <span className="ml-1 truncate">{document.path}</span>
                      </div>
                    )}
                    {document.status && (
                      <div className="flex items-center">
                        <span className="font-medium">Status:</span>
                        <span className="ml-1 capitalize">{document.status}</span>
                      </div>
                    )}
                  </div>

                  {/* Indicador de hover */}
                  <div className="mt-4 pt-4 border-t border-gray-100 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="flex items-center text-sm text-blue-600">
                      <EyeIcon className="h-4 w-4 mr-2" />
                      Clique para visualizar
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FavoritesPage;
